import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../endpoints";

// ---------- Auth ----------

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: api.LoginRequest) => api.adminLogin(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.adminLogout(),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["me"] });
    },
  });
}

// ---------- Public submissions ----------

export function useSubmitMerchantRequest() {
  return useMutation({
    mutationFn: (body: api.MerchantRequestCreate) => api.submitMerchantRequest(body),
  });
}

export function useSubmitContact() {
  return useMutation({
    mutationFn: (body: api.ContactCreate) => api.submitContact(body),
  });
}

export function useSubmitApplication() {
  return useMutation({
    mutationFn: (formData: FormData) => api.submitApplication(formData),
  });
}

// ---------- Admin: merchant requests ----------

export function useUpdateMerchantRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: api.AdminMerchantRequestUpdate }) =>
      api.updateMerchantRequest(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function usePublishMerchantRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: api.PublishRequestPayload }) =>
      api.publishMerchantRequest(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["adminPositions"] });
      qc.invalidateQueries({ queryKey: ["publicPositions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useDeclineMerchantRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string | null }) =>
      api.declineMerchantRequest(id, reason),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useArchiveMerchantRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.archiveMerchantRequest(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.removeQueries({ queryKey: ["request", id] });
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

// ---------- Admin: applications ----------

export function useUpdateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: api.AdminApplicationUpdate }) =>
      api.updateApplication(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["application", id] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useForwardApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.forwardApplication(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["application", id] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useDeclineApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string | null }) =>
      api.declineApplication(id, reason),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["application", id] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useArchiveApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.archiveApplication(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.removeQueries({ queryKey: ["application", id] });
      qc.invalidateQueries({ queryKey: ["candidates"] });
      qc.invalidateQueries({ queryKey: ["roleCandidates"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function usePromoteApplicationToCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body?: api.ApplicationPromoteInput }) =>
      api.promoteApplicationToCandidate(id, body ?? {}),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["application", id] });
      qc.invalidateQueries({ queryKey: ["candidates"] });
      qc.invalidateQueries({ queryKey: ["roleCandidates"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useUpdateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof api.updateCandidate>[1] }) =>
      api.updateCandidate(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      qc.invalidateQueries({ queryKey: ["candidate", id] });
      qc.invalidateQueries({ queryKey: ["roleCandidates"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useUploadCandidateCv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      api.uploadCandidateCv(id, formData),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      qc.invalidateQueries({ queryKey: ["candidate", id] });
      qc.invalidateQueries({ queryKey: ["roleCandidates"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useArchiveCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.archiveCandidate(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      qc.removeQueries({ queryKey: ["candidate", id] });
      qc.invalidateQueries({ queryKey: ["roleCandidates"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

// ---------- Admin: role rail ----------

export function useRunRoleIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) => api.runRoleIntake(requestId),
    onSuccess: (data, requestId) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["request", requestId] });
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
      qc.setQueryData(["roleBrief", data.id], data);
    },
  });
}

export function useCreateSearchRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleBriefId, body }: { roleBriefId: number; body: Parameters<typeof api.createSearchRun>[1] }) =>
      api.createSearchRun(roleBriefId, body),
    onSuccess: (_data, { roleBriefId }) => {
      qc.invalidateQueries({ queryKey: ["roleBrief", roleBriefId] });
      qc.invalidateQueries({ queryKey: ["searchRuns", roleBriefId] });
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useAnswerRoleClarification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleBriefId,
      clarificationId,
      answer,
    }: {
      roleBriefId: number;
      clarificationId: number;
      answer: string;
    }) => api.answerRoleClarification(roleBriefId, clarificationId, { answer }),
    onSuccess: (data, { roleBriefId }) => {
      qc.setQueryData(["roleBrief", roleBriefId], data);
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["searchRuns", roleBriefId] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useDismissRoleClarification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleBriefId,
      clarificationId,
    }: {
      roleBriefId: number;
      clarificationId: number;
    }) => api.dismissRoleClarification(roleBriefId, clarificationId),
    onSuccess: (data, { roleBriefId }) => {
      qc.setQueryData(["roleBrief", roleBriefId], data);
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["searchRuns", roleBriefId] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useReopenRoleClarification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleBriefId,
      clarificationId,
    }: {
      roleBriefId: number;
      clarificationId: number;
    }) => api.reopenRoleClarification(roleBriefId, clarificationId),
    onSuccess: (data, { roleBriefId }) => {
      qc.setQueryData(["roleBrief", roleBriefId], data);
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["searchRuns", roleBriefId] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useArchiveRoleBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.archiveRoleBrief(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.removeQueries({ queryKey: ["roleBrief", id] });
      qc.removeQueries({ queryKey: ["searchRuns", id] });
      qc.removeQueries({ queryKey: ["roleCandidates", id] });
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useCreateSourcedProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ searchRunId, body }: { searchRunId: number; body: Parameters<typeof api.createSourcedProfile>[1] }) =>
      api.createSourcedProfile(searchRunId, body),
    onSuccess: (_data, { searchRunId }) => {
      qc.invalidateQueries({ queryKey: ["sourcedProfiles", searchRunId] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useSourceOneProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ searchRunId, body }: { searchRunId: number; body?: Parameters<typeof api.sourceOneProfile>[1] }) =>
      api.sourceOneProfile(searchRunId, body ?? {}),
    onSettled: (_data, _error, variables) => {
      const searchRunId = variables?.searchRunId;
      if (!searchRunId) return;
      qc.invalidateQueries({ queryKey: ["sourcingTasks", searchRunId] });
      qc.invalidateQueries({ queryKey: ["searchRuns"] });
      qc.invalidateQueries({ queryKey: ["roleBrief"] });
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useCancelSourcingTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId }: { taskId: number; searchRunId: number }) =>
      api.cancelSourcingTask(taskId),
    onSettled: (_data, _error, variables) => {
      const searchRunId = variables?.searchRunId;
      if (!searchRunId) return;
      qc.invalidateQueries({ queryKey: ["sourcingTasks", searchRunId] });
      qc.invalidateQueries({ queryKey: ["searchRuns"] });
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useRetrySourcingTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: number; searchRunId: number; body?: Parameters<typeof api.retrySourcingTask>[1] }) =>
      api.retrySourcingTask(taskId, body ?? {}),
    onSettled: (_data, _error, variables) => {
      const searchRunId = variables?.searchRunId;
      if (!searchRunId) return;
      qc.invalidateQueries({ queryKey: ["sourcingTasks", searchRunId] });
      qc.invalidateQueries({ queryKey: ["searchRuns"] });
      qc.invalidateQueries({ queryKey: ["roleBrief"] });
      qc.invalidateQueries({ queryKey: ["roleBriefs"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useEvaluateSourcedProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: number; body: Parameters<typeof api.evaluateSourcedProfile>[1] }) =>
      api.evaluateSourcedProfile(profileId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sourcedProfiles"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useAiEvaluateSourcedProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId }: { profileId: number; searchRunId: number }) =>
      api.aiEvaluateSourcedProfile(profileId),
    onSuccess: (_data, { searchRunId }) => {
      qc.invalidateQueries({ queryKey: ["sourcedProfiles", searchRunId] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useSkipSourcedProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId }: { profileId: number; searchRunId: number }) =>
      api.skipSourcedProfile(profileId),
    onSuccess: (_data, { searchRunId }) => {
      qc.invalidateQueries({ queryKey: ["sourcedProfiles", searchRunId] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function usePromoteSourcedProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profileId, body }: { profileId: number; body: Parameters<typeof api.promoteSourcedProfile>[1] }) =>
      api.promoteSourcedProfile(profileId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sourcedProfiles"] });
      qc.invalidateQueries({ queryKey: ["roleCandidates"] });
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}

export function useCreateOutreachApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createOutreachApproval>[0]) =>
      api.createOutreachApproval(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cockpit"] });
    },
  });
}
